import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { useT } from "@/lib/i18n";

const CONTENT = {
  legal: {
    title: { en: "Legal Notice", ro: "Mentiuni Legale" },
    desc: {
      en: "Legal notice and entity information for the N3LEDGE Europe website.",
      ro: "Mentiuni legale si informatii despre entitate pentru site-ul N3LEDGE Europe.",
    },
    sections: [
      {
        h: { en: "Entity information", ro: "Informatii despre entitate" },
        p: [
          {
            en: "Legal entity name: not yet supplied.",
            ro: "Numele entitatii juridice: nu a fost inca furnizat.",
          },
          {
            en: "Registered office: not yet supplied.",
            ro: "Sediul social: nu a fost inca furnizat.",
          },
          {
            en: "Trade registry / registration number: not yet supplied.",
            ro: "Numarul de inregistrare la registrul comertului: nu a fost inca furnizat.",
          },
          {
            en: "Fiscal identification: not yet supplied.",
            ro: "Identificarea fiscala: nu a fost inca furnizata.",
          },
        ],
      },
      {
        h: { en: "Contact", ro: "Contact" },
        p: [
          {
            en: "Requests relating to this website, its content or the information in this notice can be addressed to us through the contact page or by email at contact@n3ledge.com.",
            ro: "Solicitarile legate de acest site, continutul sau sau informatiile din aceasta mentiune pot fi adresate prin pagina de contact sau prin email la contact@n3ledge.com.",
          },
        ],
      },
      {
        h: { en: "Purpose of this website", ro: "Scopul acestui site" },
        p: [
          {
            en: "This website presents N3LEDGE, a verification and financial intelligence layer that prepares verified, structured and traceable information to support financial review. The website is informational. It is not an offer, and it does not constitute financial, legal or regulatory advice.",
            ro: "Acest site prezinta N3LEDGE, un strat de verificare si inteligenta financiara care pregateste informatii verificate, structurate si trasabile pentru a sustine analiza financiara. Site-ul are caracter informativ. Nu constituie o oferta si nu reprezinta consultanta financiara, juridica sau de reglementare.",
          },
        ],
      },
      {
        h: { en: "Illustrative content", ro: "Continut ilustrativ" },
        p: [
          {
            en: "Interface examples, workflows and visualizations on this website are illustrative and conceptual. They do not contain customer data and do not represent live transaction information.",
            ro: "Exemplele de interfata, fluxurile si vizualizarile de pe acest site sunt ilustrative si conceptuale. Nu contin date de la clienti si nu reprezinta informatii de tranzactie in timp real.",
          },
        ],
      },
      {
        h: { en: "Intellectual property", ro: "Proprietate intelectuala" },
        p: [
          {
            en: "The N3LEDGE name, logo and the content of this website are the property of N3LEDGE Europe and may not be reproduced without prior written consent.",
            ro: "Numele N3LEDGE, logo-ul si continutul acestui site sunt proprietatea N3LEDGE Europe si nu pot fi reproduse fara consimtamantul scris prealabil.",
          },
        ],
      },
    ],
  },
  privacy: {
    title: { en: "Privacy Policy", ro: "Politica de Confidentialitate" },
    desc: {
      en: "How N3LEDGE Europe handles information submitted through this website.",
      ro: "Cum gestioneaza N3LEDGE Europe informatiile trimise prin acest site.",
    },
    sections: [
      {
        h: { en: "Data controller", ro: "Operator de date" },
        p: [
          {
            en: "The legal entity and registered address of the data controller have not yet been supplied. This privacy notice requires completion and legal review. Questions can be directed to contact@n3ledge.com.",
            ro: "Entitatea juridica si adresa inregistrata a operatorului de date nu au fost inca furnizate. Aceasta nota de confidentialitate necesita completare si analiza juridica. Intrebarile pot fi adresate la contact@n3ledge.com.",
          },
        ],
      },
      {
        h: { en: "What we collect", ro: "Ce colectam" },
        p: [
          {
            en: "When you request a demonstration or contact us, you provide voluntarily: your name, business email, institution, and optionally your role and a message. We do not collect information through this website by any other means.",
            ro: "Cand solicitati o demonstratie sau ne contactati, furnizati voluntar: numele, emailul de business, institutia si, optional, rolul si un mesaj. Nu colectam informatii prin acest site prin alte mijloace.",
          },
        ],
      },
      {
        h: { en: "Why we process it", ro: "De ce o prelucram" },
        p: [
          {
            en: "The information you submit is used exclusively to respond to your request and to schedule and prepare a demonstration. It is not used for advertising, is not sold and is not shared with third parties for their own purposes.",
            ro: "Informatiile trimise sunt folosite exclusiv pentru a raspunde solicitarii dvs. si pentru a programa si pregati o demonstratie. Nu sunt folosite pentru publicitate, nu sunt vandute si nu sunt partajate tertilor in scopuri proprii.",
          },
        ],
      },
      {
        h: { en: "Storage and security", ro: "Stocare si securitate" },
        p: [
          {
            en: "Submitted information is stored in a managed database environment with restricted access. Access is limited to team members who respond to requests. Retention is limited to the period necessary to handle your request and any follow-up.",
            ro: "Informatiile trimise sunt stocate intr-un mediu de baze de date gestionat, cu acces restrictionat. Accesul este limitat la membrii echipei care raspund solicitarilor. Pastrarea este limitata la perioada necesara gestionarii solicitarii si a eventualelor follow-up-uri.",
          },
        ],
      },
      {
        h: { en: "Analytics", ro: "Analize" },
        p: [
          {
            en: "This website uses privacy-oriented product analytics to understand aggregate usage of its pages. No advertising trackers are used.",
            ro: "Acest site foloseste analize de produs orientate spre confidentialitate pentru a intelege utilizarea agregata a paginilor. Nu sunt folositi trackeri de publicitate.",
          },
        ],
      },
      {
        h: { en: "Your rights", ro: "Drepturile dvs." },
        p: [
          {
            en: "You can request access to, correction of, or deletion of the information you submitted, at any time, through the contact page. We will respond within a reasonable timeframe.",
            ro: "Puteti solicita oricand, prin pagina de contact, accesul la informatiile trimise, corectarea sau stergerea lor. Vom raspunde intr-un termen rezonabil.",
          },
        ],
      },
    ],
  },
  terms: {
    title: { en: "Terms of Use", ro: "Termeni de Utilizare" },
    desc: {
      en: "Terms governing the use of the N3LEDGE Europe website.",
      ro: "Termenii care guverneaza utilizarea site-ului N3LEDGE Europe.",
    },
    sections: [
      {
        h: { en: "Acceptance", ro: "Acceptare" },
        p: [
          {
            en: "By using this website you accept these terms. If you do not accept them, please do not use the website. The applicable legal entity and governing law will be: [entity — to be completed], [jurisdiction — to be completed].",
            ro: "Prin utilizarea acestui site acceptati acesti termeni. Daca nu ii acceptati, va rugam sa nu utilizati site-ul. Entitatea juridica aplicabila si legea aplicabila vor fi: [entitate — va fi completat], [jurisdictie — va fi completata].",
          },
        ],
      },
      {
        h: { en: "Nature of the information", ro: "Natura informatiilor" },
        p: [
          {
            en: "The website describes N3LEDGE, a verification and financial intelligence layer supporting financial dossier review. Descriptions of functionality that are conceptual or planned are marked as illustrative and do not constitute contractual commitments.",
            ro: "Site-ul descrie N3LEDGE, un strat de verificare si inteligenta financiara care sustine analiza dosarelor financiare. Descrierile functionalitatilor conceptuale sau planificate sunt marcate ca ilustrative si nu constituie angajamente contractuale.",
          },
        ],
      },
      {
        h: { en: "No financial services", ro: "Nu oferim servicii financiare" },
        p: [
          {
            en: "N3LEDGE does not finance transactions, does not make financing decisions, does not execute, route or custody funds, and does not replace any financial institution. Nothing on this website is financial, investment, legal or tax advice.",
            ro: "N3LEDGE nu finanteaza tranzactii, nu ia decizii de finantare, nu executa, nu dirijeaza si nu detine fonduri si nu inlocuieste nicio institutie financiara. Nimic din acest site nu constituie consultanta financiara, de investitii, juridica sau fiscala.",
          },
        ],
      },
      {
        h: { en: "Demo requests", ro: "Solicitari de demonstratie" },
        p: [
          {
            en: "When submitting a demo request you agree to provide accurate information and to use the form for genuine business inquiries only. We may decline or discontinue contact at our discretion.",
            ro: "Cand trimiteti o solicitare de demonstratie, sunteti de acord sa furnizati informatii corecte si sa folositi formularul doar pentru cereri de business legitime. Putem refuza sau intrerupe contactul la discretia noastra.",
          },
        ],
      },
      {
        h: { en: "Liability", ro: "Raspundere" },
        p: [
          {
            en: "The website is provided as is. While we aim for accurate and current content, we do not warrant that the website is complete, error-free or uninterrupted. To the extent permitted by applicable law, liability for damages arising from the use of the website is excluded.",
            ro: "Site-ul este oferit ca atare. Desi urmarim un continut corect si actual, nu garantam ca site-ul este complet, fara erori sau neintrerupt. In masura permisa de legea aplicabila, raspunderea pentru daunele rezultate din utilizarea site-ului este exclusa.",
          },
        ],
      },
      {
        h: { en: "Changes", ro: "Modificari" },
        p: [
          {
            en: "We may update these terms from time to time. The version published on this page applies to your use of the website.",
            ro: "Putem actualiza acesti termeni periodic. Versiunea publicata pe aceasta pagina se aplica utilizarii site-ului de catre dvs.",
          },
        ],
      },
    ],
  },
};

export const LegalPage = ({ kind }) => {
  const t = useT();
  const data = CONTENT[kind];
  const paths = { legal: '/legal', privacy: '/privacy', terms: '/terms' };
  return <main id="main-content" className="main-content" tabIndex={-1}><Seo path={paths[kind]} title={`${t(data.title)} | N3LEDGE`} description={t(data.desc)} /><article className="article-layout"><div className="eyebrow-new">N3LEDGE / LEGAL</div><h1 data-testid="legal-headline">{t(data.title)}</h1><nav className="editorial-filters" aria-label={t({en:'Legal pages',ro:'Pagini legale'})}>{Object.keys(CONTENT).map(k => <Link key={k} to={paths[k]} data-testid={`legal-tab-${k}`} className={`button-secondary ${k === kind ? 'bg-soft' : ''}`} aria-current={kind === k ? 'page' : undefined}>{t(CONTENT[k].title)}</Link>)}</nav><p className="editorial-note" data-testid="legal-draft-notice">{t({en:'Legal entity details have not yet been supplied. This notice remains a draft pending the company information and legal review.',ro:'Detaliile entitatii juridice nu au fost inca furnizate. Aceasta nota ramane o ciorna in asteptarea informatiilor companiei si a analizei juridice.'})}</p>{data.sections.map((section,i) => <section key={i} data-testid={`legal-section-${i+1}`}><h2>{t(section.h)}</h2>{section.p.map((p,j) => <p key={j}>{t(p)}</p>)}</section>)}</article></main>;
};

export const LegalNotice = () => <LegalPage kind="legal" />;
export const Privacy = () => <LegalPage kind="privacy" />;
export const Terms = () => <LegalPage kind="terms" />;

export default LegalNotice;
